import {
  MAX_STACK_HEIGHT,
  cellAt,
  coordinateAt,
  type Coordinate,
  type GameState,
  type Move,
  type PassageMove,
  type Player,
} from "../../game";
import { useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";

import { moveGridFocus, positionKey, recoverGridFocus, type GridDirection } from "./gridFocus";
import "./GameBoard.css";

/**
 * Presentation/controller phase. The board treats every phase except `human-turn` as locked;
 * the match controller remains the authority for when a human may act.
 */
export type GameBoardPhase =
  "human-turn" | "bot-thinking" | "committing" | "animating" | "finished";

export type GameBoardProps = Readonly<{
  /** Canonical engine state; this component never mutates or reconstructs it. */
  state: GameState;
  /** Controller-owned source selection, if any. */
  selectedPosition?: Coordinate | null;
  /**
   * Controller-authorized moves for this revision. Omit only for a read-only board; the board
   * does not derive moves itself.
   */
  legalMoves?: readonly Move[];
  /** Locks all interaction while preserving the canonical board for inspection. */
  disabled?: boolean;
  phase?: GameBoardPhase;
  /** Player shown nearest the viewer; projection never changes canonical engine coordinates. */
  viewFrom?: Player;
  /** Adds circle/X markings for players who need a non-color visual distinction. */
  discMarkers?: boolean;
  /** Preferred focus destination after an accepted transition removes the previous field. */
  focusRecoveryTarget?: Coordinate | null;
  /** Called when a player chooses a movable source field. */
  onSelect?: (position: Coordinate) => void;
  /** Called when an invalid target activation cancels the current source selection. */
  onCancelSelection?: () => void;
  /** Called only with the exact controller-authorized move corresponding to a target activation. */
  onMove?: (move: Move) => void;
  /** Lets the controller preserve roving focus across its own state changes. */
  onFocusPosition?: (position: Coordinate) => void;
  /** A terminal passage disc retained visually by the read-only review board. */
  reviewExitedDisc?: Readonly<{ player: Player; position: Coordinate }> | null;
  /** Called when no field remains, so the controller can focus its board heading or result action. */
  onFocusRecoveryUnavailable?: () => void;
  "aria-label"?: string;
}>;

type BoardButton = HTMLButtonElement;

const DIRECTIONS: Readonly<Record<string, GridDirection>> = {
  ArrowUp: "up",
  ArrowRight: "right",
  ArrowDown: "down",
  ArrowLeft: "left",
};

export function GameBoard({
  state,
  selectedPosition = null,
  legalMoves: suppliedLegalMoves,
  disabled = false,
  phase = "human-turn",
  viewFrom = "black",
  discMarkers = false,
  focusRecoveryTarget = null,
  onSelect,
  onCancelSelection,
  onMove,
  onFocusPosition,
  onFocusRecoveryUnavailable,
  reviewExitedDisc = null,
  "aria-label": ariaLabel = "Passo game board",
}: GameBoardProps) {
  const instructionsId = useId();
  // Legal eligibility is supplied by the controller's authoritative engine query. The board does
  // not validate, synthesize, or otherwise own move rules.
  const legalMoves = suppliedLegalMoves ?? [];
  const fields = useMemo(
    () =>
      state.cells.flatMap((cell, index) => (cell.kind === "field" ? [coordinateAt(index)] : [])),
    [state.cells],
  );
  const fieldKeys = useMemo(() => new Set(fields.map(positionKey)), [fields]);
  const screenOrderedFields = useMemo(
    () =>
      [...fields].sort((left, right) => {
        const leftScreen = projectPosition(left, viewFrom);
        const rightScreen = projectPosition(right, viewFrom);
        return leftScreen.row - rightScreen.row || leftScreen.column - rightScreen.column;
      }),
    [fields, viewFrom],
  );
  const firstField = screenOrderedFields[0];
  const selectedKey = selectedPosition === null ? null : positionKey(selectedPosition);
  const selectedIsField = selectedKey !== null && fieldKeys.has(selectedKey);
  const [focusedKey, setFocusedKey] = useState<string | null>(
    selectedIsField ? selectedKey : firstField ? positionKey(firstField) : null,
  );
  const focusedPositionRef = useRef<Coordinate | null>(
    selectedIsField ? selectedPosition : (firstField ?? null),
  );
  const previousFieldsRef = useRef(fields);
  const buttonRefs = useRef(new Map<string, BoardButton>());
  const isLocked = disabled || phase !== "human-turn" || state.outcome !== null;
  const selectedPassages =
    selectedPosition === null
      ? []
      : legalMoves.filter(
          (move): move is PassageMove =>
            move.kind === "passage" && samePosition(move.from, selectedPosition),
        );
  const reviewDiscScreenPosition =
    reviewExitedDisc === null ? null : projectPosition(reviewExitedDisc.position, viewFrom);
  const reviewTargetCell =
    reviewExitedDisc === null ? null : cellAt(state, reviewExitedDisc.position);
  const reviewDiscStackLevel =
    reviewTargetCell?.kind === "field" ? reviewTargetCell.stack.length : 0;

  useLayoutEffect(() => {
    const previousFields = previousFieldsRef.current;
    previousFieldsRef.current = fields;
    const focused = focusedPositionRef.current;
    if (focused === null || fieldKeys.has(positionKey(focused))) return;

    const previousHadFocusedField = previousFields.some(
      (position) => positionKey(position) === positionKey(focused),
    );
    if (!previousHadFocusedField) return;

    const recoveredScreen = recoverGridFocus(
      projectPosition(focused, viewFrom),
      fields.map((position) => projectPosition(position, viewFrom)),
      focusRecoveryTarget === null ? undefined : projectPosition(focusRecoveryTarget, viewFrom),
    );
    const recovered = recoveredScreen ? projectPosition(recoveredScreen, viewFrom) : undefined;
    focusedPositionRef.current = recovered ?? null;
    setFocusedKey(recovered ? positionKey(recovered) : null);
    if (recovered) {
      buttonRefs.current.get(positionKey(recovered))?.focus();
      onFocusPosition?.(recovered);
    } else {
      onFocusRecoveryUnavailable?.();
    }
  }, [
    fieldKeys,
    fields,
    focusRecoveryTarget,
    onFocusPosition,
    onFocusRecoveryUnavailable,
    viewFrom,
  ]);

  useLayoutEffect(() => {
    if (isLocked || focusRecoveryTarget === null) return;
    const key = positionKey(focusRecoveryTarget);
    if (!fieldKeys.has(key)) return;
    const target = buttonRefs.current.get(key);
    if (target === undefined || document.activeElement === target) return;

    focusedPositionRef.current = focusRecoveryTarget;
    setFocusedKey(key);
    target.focus();
    onFocusPosition?.(focusRecoveryTarget);
  }, [fieldKeys, focusRecoveryTarget, isLocked, onFocusPosition]);

  const moveForTarget = (target: Coordinate): Move | undefined => {
    if (selectedPosition === null) return undefined;
    return legalMoves.find(
      (move) =>
        positionKey(move.from) === positionKey(selectedPosition) &&
        positionKey(move.to) === positionKey(target),
    );
  };

  const isMovableSource = (position: Coordinate): boolean =>
    legalMoves.some((move) => positionKey(move.from) === positionKey(position));

  const handleActivation = (position: Coordinate): void => {
    if (isLocked) return;
    const move = moveForTarget(position);
    if (move !== undefined) {
      onMove?.(move);
      return;
    }

    const cell = cellAt(state, position);
    const isSelectableSource =
      cell?.kind === "field" &&
      cell.stack.at(-1) === state.activePlayer &&
      isMovableSource(position);

    if (
      isSelectableSource &&
      (selectedPosition === null || !samePosition(position, selectedPosition))
    ) {
      onSelect?.(position);
      return;
    }

    if (selectedPosition !== null) onCancelSelection?.();
  };

  const handleKeyDown = (event: KeyboardEvent<BoardButton>, position: Coordinate): void => {
    const direction = DIRECTIONS[event.key];
    if (direction === undefined) return;

    event.preventDefault();
    const next = moveGridFocus(position, canonicalDirection(direction, viewFrom), fields);
    const nextKey = positionKey(next);
    focusedPositionRef.current = next;
    setFocusedKey(nextKey);
    buttonRefs.current.get(nextKey)?.focus();
    onFocusPosition?.(next);
  };

  const handleGridKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "Escape" || isLocked || selectedPosition === null) return;

    event.preventDefault();
    event.stopPropagation();
    onCancelSelection?.();
  };

  const handleGridClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (event.target !== event.currentTarget || isLocked || selectedPosition === null) return;

    onCancelSelection?.();
  };

  const handleBoardClick = (event: React.MouseEvent<HTMLElement>): void => {
    if (event.target !== event.currentTarget || isLocked || selectedPosition === null) return;

    onCancelSelection?.();
  };

  const registerButton = (position: Coordinate, element: BoardButton | null): void => {
    const key = positionKey(position);
    if (element) buttonRefs.current.set(key, element);
    else buttonRefs.current.delete(key);
  };

  return (
    <section
      className={`game-board${discMarkers ? " game-board--disc-markers" : ""}`}
      aria-label={ariaLabel}
      onClick={handleBoardClick}
    >
      <p className="game-board__instructions" id={instructionsId}>
        Use arrow keys to move between existing fields. Press Enter or Space to select a disc or
        make a selected move.
        {reviewExitedDisc === null
          ? ""
          : ` Review display includes the exited ${reviewExitedDisc.player} disc at its passage target.`}
      </p>
      <div
        className="game-board__grid"
        role="grid"
        aria-describedby={instructionsId}
        aria-label={
          ariaLabel === "Passo review board"
            ? "Passo review board"
            : state.outcome === null
              ? "Passo board"
              : "Passo board, game finished"
        }
        aria-colcount={5}
        aria-rowcount={5}
        onClick={handleGridClick}
        onKeyDown={handleGridKeyDown}
      >
        {Array.from({ length: 5 }, (_, row) => (
          <div aria-rowindex={row + 1} className="game-board__row" key={row} role="row">
            {Array.from({ length: 5 }, (_, column) => {
              const screenPosition = { row, column };
              const position = projectPosition(screenPosition, viewFrom);
              const targetMove = moveForTarget(position);
              const cell = cellAt(state, position);

              if (cell?.kind !== "field") {
                if (targetMove?.kind !== "passage") return null;
                return (
                  <div
                    aria-colindex={column + 1}
                    className="game-board__grid-cell game-board__grid-cell--phantom"
                    key={`passage-${positionKey(targetMove.to)}`}
                    role="gridcell"
                    style={{ gridColumn: column + 1, gridRow: row + 1 }}
                  >
                    <button
                      aria-label={passageLabel(targetMove, viewFrom)}
                      className="game-board__field game-board__field--legal-target game-board__field--passage-target game-board__field--phantom"
                      disabled={isLocked}
                      onClick={() => onMove?.(targetMove)}
                      type="button"
                    />
                  </div>
                );
              }

              const key = positionKey(position);
              const isSelected = selectedKey === key;
              const movable =
                !isLocked && cell.stack.at(-1) === state.activePlayer && isMovableSource(position);
              const isLegalTarget = !isLocked && targetMove !== undefined;
              const stackHeight = cell.stack.length;
              const full = stackHeight === MAX_STACK_HEIGHT;
              const buried = stackHeight > 1;
              const focusable =
                focusedKey === key || (focusedKey === null && firstField === position);
              const label = fieldLabel(screenPosition, cell.stack, {
                selected: isSelected,
                movable,
                legalTarget: isLegalTarget,
                full,
                locked: isLocked,
              });

              return (
                <div
                  aria-colindex={screenPosition.column + 1}
                  aria-selected={isSelected}
                  className="game-board__grid-cell"
                  data-position={key}
                  key={key}
                  role="gridcell"
                  style={{
                    gridColumn: screenPosition.column + 1,
                    gridRow: screenPosition.row + 1,
                  }}
                >
                  <button
                    aria-disabled={isLocked}
                    aria-label={label}
                    className={[
                      "game-board__field",
                      movable && "game-board__field--selectable",
                      isSelected && "game-board__field--selected",
                      isLegalTarget && "game-board__field--legal-target",
                      targetMove?.kind === "passage" && "game-board__field--passage-target",
                      full && "game-board__field--full",
                      buried && "game-board__field--buried",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    disabled={isLocked}
                    onClick={() => handleActivation(position)}
                    onFocus={() => {
                      focusedPositionRef.current = position;
                      setFocusedKey(key);
                      onFocusPosition?.(position);
                    }}
                    onKeyDown={(event) => handleKeyDown(event, position)}
                    ref={(element) => registerButton(position, element)}
                    tabIndex={focusable ? 0 : -1}
                    type="button"
                  >
                    <span aria-hidden="true" className="game-board__tile-face" />
                    <span aria-hidden="true" className="game-board__stack">
                      {cell.stack.map((player, index) => (
                        <span
                          className={`game-board__disc game-board__disc--${player}`}
                          key={`${player}-${index}`}
                          style={{ "--stack-level": index } as React.CSSProperties}
                        >
                          <span className="game-board__disc-side" />
                          <span className="game-board__disc-face" />
                        </span>
                      ))}
                    </span>
                    {isSelected ? (
                      <span aria-hidden="true" className="game-board__selection-marker" />
                    ) : null}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
        {selectedPassages
          .filter((move) => !isScreenBoardPosition(projectPosition(move.to, viewFrom)))
          .map((move) => {
            const target = projectPosition(move.to, viewFrom);
            return (
              <button
                aria-label={passageLabel(move, viewFrom)}
                className="game-board__field game-board__field--legal-target game-board__field--passage-target game-board__field--phantom game-board__field--outside"
                disabled={isLocked}
                key={`outside-passage-${positionKey(move.to)}`}
                onClick={() => onMove?.(move)}
                style={
                  {
                    "--passage-left": passageOffset(target.column),
                    "--passage-top": passageOffset(target.row),
                  } as React.CSSProperties
                }
                type="button"
              />
            );
          })}
        {reviewExitedDisc !== null && reviewDiscScreenPosition !== null ? (
          <span
            aria-hidden="true"
            className="game-board__review-disc"
            style={
              {
                "--review-disc-left": passageOffset(reviewDiscScreenPosition.column),
                "--review-disc-top": passageOffset(reviewDiscScreenPosition.row),
              } as React.CSSProperties
            }
          >
            <span aria-hidden="true" className="game-board__stack">
              <span
                className={`game-board__disc game-board__disc--${reviewExitedDisc.player}`}
                style={{ "--stack-level": reviewDiscStackLevel } as React.CSSProperties}
              >
                <span className="game-board__disc-side" />
                <span className="game-board__disc-face" />
              </span>
            </span>
          </span>
        ) : null}
        <div aria-hidden="true" className="game-board__overlay" />
      </div>
    </section>
  );
}

function samePosition(left: Coordinate, right: Coordinate): boolean {
  return left.row === right.row && left.column === right.column;
}

function passageDirection(move: PassageMove, viewFrom: Player): string {
  const from = projectPosition(move.from, viewFrom);
  const to = projectPosition(move.to, viewFrom);
  if (to.column < from.column) return "Forward left";
  if (to.column > from.column) return "Forward right";
  return "Straight forward";
}

function passageLabel(move: PassageMove, viewFrom: Player): string {
  return `${passageDirection(move, viewFrom)} passage; leave the board and win`;
}

function isScreenBoardPosition(position: Coordinate): boolean {
  return position.row >= 0 && position.row < 5 && position.column >= 0 && position.column < 5;
}

function passageOffset(index: number): string {
  if (index === 0) return "var(--board-padding)";
  const operation = index > 0 ? "+" : "-";
  const steps = Array.from(
    { length: Math.abs(index) },
    () => `${operation} var(--field-size) ${operation} var(--tile-gap)`,
  ).join(" ");
  return `calc(var(--board-padding) ${steps})`;
}

type FieldLabelState = Readonly<{
  selected: boolean;
  movable: boolean;
  legalTarget: boolean;
  full: boolean;
  locked: boolean;
}>;

function fieldLabel(
  screenPosition: Coordinate,
  stack: readonly Player[],
  { selected, movable, legalTarget, full, locked }: FieldLabelState,
): string {
  const location = `${screenRowName(screenPosition.row)}, column ${screenPosition.column + 1} from left`;
  const contents =
    stack.length === 0
      ? "Empty field"
      : `Stack, bottom to top: ${stack
          .map((player, index) => discDescription(player, index, stack.length))
          .join(", then ")}`;
  const state = [
    `Height ${stack.length} of ${MAX_STACK_HEIGHT}`,
    full ? "full capacity" : null,
    selected ? "selected source" : null,
    legalTarget ? "legal destination" : null,
    movable ? "movable top disc" : null,
    locked ? "interaction locked" : null,
  ].filter((part): part is string => part !== null);
  return `${location}. ${contents}. ${state.join(", ")}.`;
}

function projectPosition(position: Coordinate, viewFrom: Player): Coordinate {
  return viewFrom === "black" ? position : { row: 4 - position.row, column: 4 - position.column };
}

function canonicalDirection(direction: GridDirection, viewFrom: Player): GridDirection {
  if (viewFrom === "black") return direction;
  const opposite: Readonly<Record<GridDirection, GridDirection>> = {
    up: "down",
    right: "left",
    down: "up",
    left: "right",
  };
  return opposite[direction];
}

function screenRowName(row: number): string {
  switch (row) {
    case 0:
      return "Far row";
    case 1:
      return "Second row from far edge";
    case 2:
      return "Middle row";
    case 3:
      return "Second row from near edge";
    case 4:
      return "Near row";
    default:
      throw new RangeError(`Screen row ${row} is outside the board.`);
  }
}

function discDescription(player: Player, index: number, height: number): string {
  const placement =
    height === 1 ? "only disc" : index === 0 ? "bottom" : index === height - 1 ? "top" : "middle";
  return `${player} player, ${placement}`;
}
